import {
    LightComponent,
    MeshComponent,
    Object3D,
    PhysXComponent,
} from '@wonderlandengine/api';
import {vec3} from 'gl-matrix';
import {ActiveCamera} from './active-camera.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {Blink} from './blink.js';
import {WasdControlsFps} from './wasd-controls-fps.js';
import {Locomotion} from './locomotion.js';
import {Teleport} from './teleport.js';
import {Inspect} from './inspect.js';
/**
 * inspect-world
 */
export class InspectWorld extends Inspect {
    static TypeName = 'inspect-world';
    static InheritProperties = true;
    /* Properties that are configurable in the editor */
    @property.object({required: true})
    inspectSphere!: Object3D; /* The sphere that will be closed on us, exists in Player's child */

    @property.object({required: true})
    inspectLight!: Object3D; /* The sphere that will be closed on us, exists in Player's child */

    @property.object({required: true})
    locomotionObject!: Object3D;

    private headObject?: Object3D;
    private inspectWorldPosDefault = vec3.create();
    private inspectedObjTransform = vec3.create();
    private playerBlink?: Blink;
    private playerPhysx?: PhysXComponent;
    private playerWasd?: WasdControlsFps;
    private playerLocomotion?: Locomotion;
    private inspectSphereMesh?: MeshComponent;
    private inspectLightLight?: LightComponent;
    private teleport?: Teleport;

    start() {
        /* General */
        const tempBlink = this.player.getComponent(Blink);
        if (!tempBlink) {
            throw new Error(
                `inspect-world(${this.object.name}): blink component is not defined on player`
            );
        }
        this.playerBlink = tempBlink;
        const tempPhysx = this.player.getComponent(PhysXComponent);
        if (!tempPhysx) {
            throw new Error(
                `inspect-world(${this.object.name}): physx component is not defined on player`
            );
        }
        this.playerPhysx = tempPhysx;
        const tempWasd = this.player.getComponent(WasdControlsFps);
        if (!tempWasd) {
            throw new Error(
                `inspect-world(${this.object.name}): wasd-controls-fps component is not defined on player`
            );
        }
        this.playerWasd = tempWasd;
        const tempTeleport = this.player.getComponent(Teleport);
        if (!tempTeleport) {
            throw new Error(
                `inspect-world(${this.object.name}): teleport component is not defined on player`
            );
        }
        this.teleport = tempTeleport;

        const activeCamera = this.player.getComponent(ActiveCamera);
        if (!activeCamera) {
            throw new Error(
                `inspect-world(${this.object.name}): active-camera component required on Player`
            );
        }

        const tempSphereMesh = this.inspectSphere!.getComponent(MeshComponent);
        if (!tempSphereMesh) {
            throw new Error(
                `inspect-world(${this.object.name}): mesh component is not defined on inspectSphere`
            );
        }

        const tempLightLight = this.inspectLight.getComponent(LightComponent);
        if (!tempLightLight) {
            throw new Error(
                `inspect-world(${this.object.name}): light component is not defined on inspectLight`
            );
        }
        this.inspectLightLight = tempLightLight;

        if (!this.locInspectedObj) {
            throw new Error(
                `inspect-world(${this.object.name}): locInspectedObj is not defined`
            );
        }

        if (!this.locomotionObject) {
            throw new Error(
                `inspect-world(${this.object.name}): locomotionObject is not defined`
            );
        }
        const tempLocomotion = this.locomotionObject.getComponent(Locomotion);
        if (!tempLocomotion) {
            throw new Error(
                `inspect-world(${this.object.name}): locomotion component is not defined on locomotionObject`
            );
        }
        this.playerLocomotion = tempLocomotion;

        this.headObject = activeCamera.getActiveCamera();
        //this.inspectLight = this.inspectSphere.children[0];
        this.inspectWorldPosDefault = [this.engine.xr ? 30 : 10, 0, 0];
    }

    async enterInspectWorld(inspectedObj: Object3D, inspectWorldPos: vec3) {
        /* Set Position to Default if Null */
        if (!inspectWorldPos) {
            this.inspectWorldPosDefault = [this.engine.xr ? 30 : 10, 0, 0];
        }

        /* Store Transform */
        this.inspectedObjTransform = inspectedObj.getTransformWorld();

        /* Blink & Change Locations of Inspected Obj & Player */
        await this.playerBlink!.startBlink(() => {
            this.playerWasd!.active = false; /* Disable Movement */
            this.playerLocomotion!.active = false; /* Disable Movement */

            this.teleport!.teleport(inspectWorldPos); /* Teleport Player to Inspect World */

            /* Inspected Sphere & Light, Activate & Set Locations */
            this.inspectSphereMesh!.active = true;
            this.inspectLightLight!.active = true;
            this.setTransformInfrontOf(
                this.player,
                this.inspectLight,
                5
            ); /* Inspected Light Transform */
        });
    }

    async exitInspectWorld(inspectedObj: Object3D) {
        /* Blink & Change Locations of Inspected Obj & Player */
        await this.playerBlink!.startBlink(() => {
            inspectedObj.setTransformWorld(this.inspectedObjTransform);

            /* Stop Rotation, & Disable InspectSphere */
            this.inspectSphereMesh!.active = false;
            this.inspectLightLight!.active = false;

            /* Re-enable Movement */
            this.playerWasd!.active = true;
            this.playerLocomotion!.active = true;

            this.teleport!.back();
        });
    }
}
